import { toast } from "sonner";

import { LoadingScreen } from "@/components/loading-screen";
import { useGetSessionByKey } from "@/features/session/api/get-session-by-key";
import { useVidyoStore } from "@/features/vidyo-call/utils/vidyo-store";
import { DndContext, DragEndEvent } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";

import { useGetBreakoutSessions } from "../api/get-breakout-sessions";
import { useMoveParticipantToBreakoutSession } from "../api/move-participant";
import { ParticipantWithSessionId } from "../types/participant-with-session-id";

import { BreakoutSessionCard } from "./breakout-session-card";
import { CloseRoomsDialog } from "./close-rooms-modal";

export function BreakoutSessionsList() {
  const sessionId = useVidyoStore((store) => store.sessionId);
  const roomKey = useVidyoStore((store) => store.roomKey);
  const {
    data: breakoutSessions,
    isLoading,
    isSuccess,
  } = useGetBreakoutSessions(sessionId);

  const {
    data: session,
    isLoading: isSessionLoading,
    isSuccess: isSessionSuccess,
  } = useGetSessionByKey(roomKey ?? "");
  const currentRoomKey = useVidyoStore((store) => store.roomKey);

  const removeParticipant = useVidyoStore(
    (store) => store.breakoutSession.removeParticipant,
  );

  const updateBreakoutSessionParticipants = useVidyoStore(
    (store) => store.breakoutSession.updateBreakoutSessionParticipants,
  );

  const { mutateAsync: moveParticipantToBreakoutSession } =
    useMoveParticipantToBreakoutSession({
      onMutate: (options) => {
        const removedParticipant = participants.find(
          (p) => p.participantId === options.body.participantId,
        );

        removeParticipant(options.body.participantId);
        toast.success("Participant will be moved to the session!");

        // Return the removed participant data for use in onError
        return { removedParticipant };
      },
      onError: (_error, _variables, ctx) => {
        // Restore the participant if we have the data
        const context = ctx as
          | { removedParticipant: ParticipantWithSessionId | undefined }
          | undefined;

        if (context?.removedParticipant) {
          updateBreakoutSessionParticipants({
            sessionId: context.removedParticipant.sessionId,
            participants: [
              {
                participantID: context.removedParticipant.participantId,
                displayName: context.removedParticipant.name,
                isHandRaised: false,
                handRaisedAt: "",
                entityId: "",
                extension: "",
                isAudioEnabled: true,
                isVideoEnabled: true,
              },
            ],
          });
        }
        toast.error("Failed to move participant to breakout session");
      },
    });
  const participants = useVidyoStore(
    (store) => store.breakoutSession.participants,
  );

  if (isLoading || isSessionLoading) {
    return <LoadingScreen />;
  }

  if (!isSuccess || !isSessionSuccess) {
    return (
      <p className="text-center text-muted-foreground">
        Error fetching breakout sessions
      </p>
    );
  }

  const isBreakoutSession = session.parentSession !== null;

  const mainCallSessionId = session.parentSession
    ? session.parentSession.id
    : session.id;

  const handleDragEnd = async (event: DragEndEvent) => {
    const participantId = event.active.id;
    const breakoutSessionId = event.over?.id.toString() ?? "";
    const participant = participants.find(
      (p) => p.participantId === participantId,
    );

    if (!participant) {
      throw new Error(
        `[BreakoutSessionsList] Participant not found: ${participantId}`,
      );
    }

    if (participant.sessionId === breakoutSessionId) {
      return;
    }

    await moveParticipantToBreakoutSession({
      // this is the sessionId where we send out the request to move the participant
      sessionId: participant.sessionId,
      body: {
        participantId: participantId.toString(),
        entityId: participant.entityId,
        // this is the sessionId where the participant will be moved to
        targetSessionId: breakoutSessionId,
      },
    });
  };

  return (
    <div className="px-4">
      <div className="mb-4 flex justify-center">
        <CloseRoomsDialog />
      </div>
      <DndContext
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}
      >
        <div className="flex flex-col gap-4">
          <BreakoutSessionCard
            name="Main call"
            roomKey={session.parentSession?.roomKey ?? ""}
            sessionId={mainCallSessionId}
            disabled={!isBreakoutSession}
          />

          {breakoutSessions.map((breakoutSession) => (
            <BreakoutSessionCard
              key={breakoutSession.id}
              name={breakoutSession.name}
              roomKey={breakoutSession.room.key}
              sessionId={breakoutSession.id}
              disabled={currentRoomKey === breakoutSession.room.key}
            />
          ))}
        </div>
      </DndContext>
    </div>
  );
}
